import { useState } from 'react';

import { Alert, Card, Input, Radio, Table, Tag, Tooltip, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';

import {
	ReservationAudit,
	ShopFloorMaterialNeed,
	useGetShopFloorMaterialNeedsQuery,
	useGetShopFloorReservationAuditSummaryQuery,
	useGetShopFloorReservationAuditsQuery,
} from '../../../redux/api/shop-floor-needs';

const { Text, Title } = Typography;

const BUCKET_LABELS: Record<string, string> = {
	original_bar: 'Original bar',
	original_material: 'Same material',
	substitute_certified: 'Substitute (certified)',
	substitute_uncertified: 'Substitute (no certificate)',
};

const NEED_STATUS_COLORS: Record<string, string> = {
	Open: 'orange',
	Fulfilled: 'green',
	Dismissed: 'default',
	'Auto Cleared': 'blue',
};

/**
 * Shop-manager view of extra material requested from the machines.
 *
 * Substitution is the risky part of this flow: a different material has a
 * different cost per cubic inch, and one without a certificate has compliance
 * consequences. Both would otherwise be invisible once the bar is cut.
 */
const ShopFloorMaterialNeeds: React.FC = () => {
	const [needStatus, setNeedStatus] = useState('Open');
	const [auditFilter, setAuditFilter] = useState('all');
	const [search, setSearch] = useState('');

	const { data: summary } = useGetShopFloorReservationAuditSummaryQuery();
	const { data: needs, isFetching: loadingNeeds } =
		useGetShopFloorMaterialNeedsQuery({
			status: needStatus,
			...(search ? { search } : {}),
		});
	const { data: auditsData, isFetching: loadingAudits } =
		useGetShopFloorReservationAuditsQuery({
			...(auditFilter === 'substitutions'
				? { bucket: 'substitute_certified' }
				: {}),
			...(auditFilter === 'uncertified'
				? { bucket: 'substitute_uncertified' }
				: {}),
			...(search ? { search } : {}),
		});

	const audits: ReservationAudit[] = Array.isArray(auditsData)
		? auditsData
		: auditsData?.results || [];

	const needColumns = [
		{
			title: 'Job',
			key: 'job',
			render: (need: ShopFloorMaterialNeed) => (
				<>
					<div>
						<Text strong>{need.order_line_item.order_name || '—'}</Text>
					</div>
					<Text type="secondary">{need.order_line_item.name || '—'}</Text>
				</>
			),
		},
		{
			title: 'Pieces',
			key: 'pieces',
			render: (need: ShopFloorMaterialNeed) =>
				need.remaining_quantity === need.requested_quantity
					? need.requested_quantity
					: `${need.remaining_quantity} of ${need.requested_quantity} left`,
		},
		{
			title: 'Reason',
			key: 'reason',
			render: (need: ShopFloorMaterialNeed) => need.reason_label || '—',
		},
		{
			title: 'Machine',
			key: 'machine',
			render: (need: ShopFloorMaterialNeed) =>
				need.machine_name || need.order_line_item.machine_name || '—',
		},
		{
			// This page is deliberately every-building, unlike the saw tablet.
			// A manager comparing sites needs the column to tell them apart, and
			// needs to spot the ones that failed to resolve.
			title: 'Building',
			key: 'building',
			render: (need: ShopFloorMaterialNeed) => {
				if (need.building_name) return need.building_name;
				return (
					<Tooltip
						title={
							need.source_building_name
								? `The Shop Floor App reported "${need.source_building_name}", which does not match a building here. This request shows on every saw tablet.`
								: 'Arrived without a building, so it shows on every saw tablet.'
						}
					>
						<Tag color="warning">
							<WarningOutlined /> Unknown
						</Tag>
					</Tooltip>
				);
			},
		},
		{
			title: 'Requested by',
			key: 'requested_by',
			render: (need: ShopFloorMaterialNeed) => need.requested_by_name || '—',
		},
		{
			title: 'Requested',
			key: 'created',
			render: (need: ShopFloorMaterialNeed) =>
				new Date(need.created_time).toLocaleString(),
		},
		{
			title: 'Status',
			key: 'status',
			render: (need: ShopFloorMaterialNeed) => (
				<Tag color={NEED_STATUS_COLORS[need.status] || 'default'}>
					{need.status}
				</Tag>
			),
		},
		{
			title: 'Notes',
			key: 'notes',
			render: (need: ShopFloorMaterialNeed) =>
				need.notes ? (
					<Tooltip title={need.notes}>
						<Text ellipsis style={{ maxWidth: 180, display: 'block' }}>
							{need.notes}
						</Text>
					</Tooltip>
				) : (
					'—'
				),
		},
	];

	const auditColumns = [
		{
			title: 'Job',
			key: 'job',
			render: (audit: ReservationAudit) => (
				<>
					<div>
						<Text strong>{audit.order_name || '—'}</Text>
					</div>
					<Text type="secondary">{audit.part_name || '—'}</Text>
				</>
			),
		},
		{
			title: 'Material used',
			key: 'material',
			render: (audit: ReservationAudit) => (
				<>
					<div>{audit.material_display || '—'}</div>
					<Text type="secondary">{audit.material_friendly_id || '—'}</Text>
				</>
			),
		},
		{
			title: 'Option used',
			key: 'bucket',
			render: (audit: ReservationAudit) => (
				<Tag
					color={
						audit.bucket === 'substitute_uncertified'
							? 'red'
							: audit.is_substitution
							? 'orange'
							: 'green'
					}
				>
					{BUCKET_LABELS[audit.bucket] || audit.bucket}
				</Tag>
			),
		},
		{
			title: 'Cut / qty',
			key: 'quantity',
			render: (audit: ReservationAudit) =>
				`${audit.quantity ?? '—'} pcs @ ${audit.material_cut_length ?? '—'} in`,
		},
		{
			title: 'Reserved by',
			key: 'operator',
			render: (audit: ReservationAudit) => audit.operator_name || '—',
		},
		{
			title: 'Requested by',
			key: 'requested_by',
			render: (audit: ReservationAudit) =>
				audit.need?.requested_by_name || '—',
		},
		{
			title: 'Machine',
			key: 'machine',
			render: (audit: ReservationAudit) => audit.need?.machine_name || '—',
		},
		{
			title: 'Certificate',
			key: 'certificate',
			render: (audit: ReservationAudit) => {
				if (audit.bucket !== 'substitute_uncertified') {
					return <Tag color="green">On file</Tag>;
				}
				return audit.certificate_acknowledged ? (
					<Tooltip
						title={`Acknowledged by ${
							audit.certificate_acknowledged_by || 'unknown'
						} on ${
							audit.certificate_acknowledged_at
								? new Date(
										audit.certificate_acknowledged_at
								  ).toLocaleString()
								: 'unknown date'
						}`}
					>
						<Tag color="warning" icon={<WarningOutlined />}>
							Missing — acknowledged
						</Tag>
					</Tooltip>
				) : (
					<Tag color="red">Missing</Tag>
				);
			},
		},
		{
			title: 'When',
			key: 'created',
			render: (audit: ReservationAudit) =>
				new Date(audit.created_time).toLocaleString(),
		},
	];

	return (
		<div className="flex flex-col w-full" style={{ padding: 16 }}>
			<Title level={3}>Shop Floor Material Requests</Title>
			<Text type="secondary">
				Extra material requested by machine operators, and the reservations
				made to cover it.
			</Text>

			<div className="flex gap-4 flex-wrap" style={{ margin: '16px 0' }}>
				<Card size="small" style={{ minWidth: 160 }}>
					<Text type="secondary">Open requests</Text>
					<Title level={3} style={{ margin: 0 }}>
						{summary?.open_needs ?? '—'}
					</Title>
				</Card>
				<Card size="small" style={{ minWidth: 160 }}>
					<Text type="secondary">Extras reserved</Text>
					<Title level={3} style={{ margin: 0 }}>
						{summary?.total ?? '—'}
					</Title>
				</Card>
				<Card size="small" style={{ minWidth: 160 }}>
					<Text type="secondary">Substitutions</Text>
					<Title level={3} style={{ margin: 0 }}>
						{summary?.substitutions ?? '—'}
					</Title>
				</Card>
				<Card size="small" style={{ minWidth: 160 }}>
					<Text type="secondary">No certificate</Text>
					<Title level={3} style={{ margin: 0, color: '#cf1322' }}>
						{summary?.uncertified ?? '—'}
					</Title>
				</Card>
			</div>

			{!!summary?.substitutions && (
				<Alert
					type="info"
					showIcon
					style={{ marginBottom: 16 }}
					message="A substitute material changes job cost"
					description="Substitutes have a different cost per cubic inch. The job cost ledger records it when the bar is cut, so these jobs will move against their estimate."
				/>
			)}

			<Input.Search
				allowClear
				placeholder="Job number, part, operator or machine"
				style={{ maxWidth: 420, marginBottom: 16 }}
				onSearch={(value) => setSearch(value || '')}
			/>

			<Card
				title="Requests"
				extra={
					<Radio.Group
						value={needStatus}
						onChange={(e) => setNeedStatus(e.target.value)}
						optionType="button"
					>
						<Radio.Button value="Open">Open</Radio.Button>
						<Radio.Button value="Fulfilled">Fulfilled</Radio.Button>
						<Radio.Button value="Dismissed">Dismissed</Radio.Button>
						<Radio.Button value="Auto Cleared">Auto cleared</Radio.Button>
					</Radio.Group>
				}
				style={{ marginBottom: 24 }}
			>
				<Table
					rowKey="id"
					size="small"
					loading={loadingNeeds}
					dataSource={needs || []}
					columns={needColumns}
					pagination={{ pageSize: 20 }}
				/>
			</Card>

			<Card
				title="Reservations made from the floor"
				extra={
					<Radio.Group
						value={auditFilter}
						onChange={(e) => setAuditFilter(e.target.value)}
						optionType="button"
					>
						<Radio.Button value="all">All</Radio.Button>
						<Radio.Button value="substitutions">Substitutions</Radio.Button>
						<Radio.Button value="uncertified">No certificate</Radio.Button>
					</Radio.Group>
				}
			>
				<Table
					rowKey="id"
					size="small"
					loading={loadingAudits}
					dataSource={audits}
					columns={auditColumns}
					pagination={{ pageSize: 20 }}
					scroll={{ x: 1400 }}
				/>
			</Card>
		</div>
	);
};

export default ShopFloorMaterialNeeds;
